import React, { useState } from 'react'
import { Col, Modal, notification, Row, Switch } from 'antd'
import { Formik } from 'formik'

import { AssignTree, PAssignTreeType } from 'src/types'
import {
  Input,
  SelectOrganization,
  SelectResource
} from 'src/components/Formik'
import { useT } from 'src/core/hooks/use-translation'
import * as TreesService from 'src/services/api/trees'

import { initialValues } from './initial-values'
import * as CSS from './styles'
import sanitize from './sanitize'
import validationSchema from './validation-schema'

interface ModalTreeNodeOptionProps {
  formikProps: any
}

function ModalTreeNodeOption({ formikProps }: ModalTreeNodeOptionProps) {
  switch (formikProps.values.p_assign_tree_type) {
    case PAssignTreeType.REASON:
      return (
        <SelectResource
          defaultActiveFirstOption
          formikProps={formikProps}
          keyLabel="name"
          keyValue="reason_id"
          label="web.resources.reasons.title"
          name="reason_id"
          resource="reasons"
        />
      )
    case PAssignTreeType.COST_CENTER:
      return (
        <SelectResource
          defaultActiveFirstOption
          formikProps={formikProps}
          keyLabel="name"
          keyValue="cost_center_id"
          label="web.resources.cost-centers.title"
          name="cost_center_id"
          resource="cost_centers"
        />
      )
    case PAssignTreeType.TENANT:
      return (
        <SelectOrganization
          defaultActiveFirstOption
          formikProps={formikProps}
          label="web.resources.organizations.title"
        />
      )
    default:
      return <></>
  }
}

interface ModalTreeNodeProps {
  refFormik: React.MutableRefObject<any>
  updateNode: (node: AssignTree) => void
}

export default function ModalTreeNode({
  refFormik,
  updateNode
}: ModalTreeNodeProps) {
  const [isLoading, setIsLoading] = useState(false)
  const { t } = useT()

  async function handleSubmit(values: typeof initialValues) {
    setIsLoading(true)

    const sanitized = sanitize(values)
    const response = await TreesService.saveNode(sanitized)
    setIsLoading(false)

    if (response.status !== 200) {
      notification.warning({
        message: t('common.response.status-500'),
        placement: 'bottomRight'
      })
      return false
    }

    if (response.data.trees?.length) {
      const newRecord: AssignTree = { ...response.data.trees[0], childrens: [] }
      updateNode(newRecord)
    }

    refFormik.current?.resetForm({})
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      ref={refFormik}
      render={formikProps => {
        if (!formikProps.values.tenant_id) {
          return <span />
        }

        const title = t('web.tree.modal.title')
        const isVisible = Boolean(formikProps?.values?.tenant_id)

        return (
          <Modal
            centered
            closable
            okButtonProps={{
              loading: isLoading
            }}
            cancelButtonProps={{
              loading: isLoading
            }}
            onCancel={() => {
              refFormik.current?.resetForm({})
            }}
            onOk={() => {
              refFormik.current?.handleSubmit()
            }}
            title={title}
            visible={isVisible}
            width={700}
          >
            <Row>
              <Col span={24}>
                <span>{t('web.tree.modal.message')}</span>
              </Col>
            </Row>
            <Row>
              <Col lg={20} span={24}>
                <CSS.ParentNode>
                  {formikProps.values?.parent_name}
                </CSS.ParentNode>
              </Col>
            </Row>
            <Row align="middle" gutter={24} justify="space-between">
              <Col span={12}>
                <ModalTreeNodeOption formikProps={formikProps} />
              </Col>
            </Row>
            <Row align="middle" gutter={24} justify="space-between">
              <Col span={10}>
                <Input
                  data-test-id="node-code"
                  formikProps={formikProps}
                  label="web.tree.node.code"
                  name="code"
                  placeholder="web.tree.node.code-placeholder"
                  required={false}
                />
              </Col>
              <Col span={14}>
                <Input
                  data-test-id="node-name"
                  formikProps={formikProps}
                  label="web.tree.node.name"
                  name="assign_tree_name"
                  placeholder="web.tree.node.name-placeholder"
                />
              </Col>
            </Row>
            <Row align="middle" gutter={24} justify="space-between">
              <Col span={10}>
                <div
                  onClick={() => {
                    formikProps.setFieldValue(
                      'chained',
                      !formikProps.values.chained
                    )
                  }}
                >
                  <CSS.Text>{t('web.tree.node.chained')}:</CSS.Text>
                  <Switch defaultChecked={formikProps.values.chained} />
                </div>
              </Col>
              <Col span={14}>
                <div
                  onClick={() => {
                    formikProps.setFieldValue(
                      'heritable_properties',
                      !formikProps.values.heritable_properties
                    )
                  }}
                >
                  <CSS.Text>{t('web.tree.node.heritable')}:</CSS.Text>
                  <Switch
                    defaultChecked={formikProps.values.heritable_properties}
                  />
                </div>
              </Col>
            </Row>
          </Modal>
        )
      }}
      validationSchema={validationSchema}
    />
  )
}
